import { AxiosError } from 'axios';
import { ShiftReportData } from 'Interfaces/VisibilityBoard';
import { makeAutoObservable } from 'mobx';
import { toast } from 'react-toastify';
import { VisibilityBoardServices } from 'services/visibilityBoardServices';

export class VisibilityBoardStore {
  data: ShiftReportData = {
    bph: [],
    clean: [],
    comments: []
  };

  selectedShift: string = 'Second';
  selectedRoom: string = 'GO';

  isLoading: boolean = false;
  isPolling: boolean = false;
  environment = import.meta.env.VITE_ENVIRONMENT;

  constructor() {
    makeAutoObservable(this);
  }

  setData = (data: ShiftReportData) => {
    this.data = data;
  };

  setSelectedShift = (shift: string) => {
    this.selectedShift = shift;
    this.fetchVisibilityBoardData();
  };

  setSelectedRoom = (room: string) => {
    this.selectedRoom = room;
    this.fetchVisibilityBoardData();
  };

  // Initial load method (with loading indicator)
  fetchVisibilityBoardData = async () => {
    this.isLoading = true;
    try {
      const data = await VisibilityBoardServices.getVisibilityBoardData({
        room: this.selectedRoom,
        shift: this.selectedShift
      });

      // Check if response contains error message
      if ('success' in data.data && !data.data.success) {
        throw new AxiosError(data.data.message);
      }

      this.setData(data.data as ShiftReportData);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      this.isLoading = false;
    }
  };

  // Polling method (without loading indicator)
  pollVisibilityBoardData = async () => {
    this.isPolling = true;
    try {
      const data = await VisibilityBoardServices.getVisibilityBoardData({
        room: this.selectedRoom,
        shift: this.selectedShift
      });

      // Check if response contains error message
      if ('success' in data.data && !data.data.success) {
        throw new AxiosError(data.data.message);
      }

      // Update data only if it has changed
      if (JSON.stringify(this.data) !== JSON.stringify(data.data)) {
        this.setData(data.data as ShiftReportData);
      }
    } catch (error: any) {
      // Don't show toast for polling errors to avoid spam
      if(this.environment === 'development') {
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling!');
    } finally {
      this.isPolling = false;
    }
  };

  // Method to refresh data (can be called manually)
  refreshData = async () => {
    await this.fetchVisibilityBoardData();
  };

  // Calculate total BPH for the shift
  get totalBph() {
    return this.data.bph.reduce((total, item) => total + item.totalBphOfJobs, 0);
  }

  // Calculate average actual BPH (if available)
  get averageActualBph() {
    const items = this.data.bph.filter(item => item.actual !== undefined);
    if (items.length === 0) {
      return 0;
    }
    return items.reduce((total, item) => total + (item.actual || 0), 0) / items.length;
  }

  // Calculate total variance
  get totalVariance() {
    return this.data.bph.reduce((total, item) => {
      const variance = item.variance || 0;
      return total + variance;
    }, 0);
  }

  // Transform clean data for display in the changeovers table
  get transformedCleanData() {
    return this.data.clean.map(item => {
      const totalChangeoverR2 = item.clean + item.clear + item.setup + item.lv;

      return {
        ...item,
        totalChangeoverR2,
        sections: [
          { label: 'Clean', value: item.clean },
          { label: 'Clear', value: item.clear },
          { label: 'Setup', value: item.setup },
          { label: 'LV', value: item.lv }
        ]
      };
    });
  }

  // Print report
  printReport() {
    window.print();
  }
}
